import { useEffect, useRef, useState } from "react";
import { differenceInMinutes, format } from "date-fns";
import { TableRow, TableCell, Collapse, IconButton, Box } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ReplayIcon from "@mui/icons-material/Replay";

import { MapViewer } from "../../Form/MapViewer";
import { Progress } from "../../templates/Progress";
import { CleaningRobotItem } from "types/response/robot";
import { CleaningMapItem, DEFAULT_DISPLAY_SETTING } from "types/response/map";
import { CleaningEventItem } from "types/response/cleaningEvent";
import {
  MAP_DELETED_LABEL,
  ROBOT_DELETED_LABEL,
} from "constraints/constraints";
import { callSyncCleaningEvent } from "api/worker/cleaningEvent";
import { notify } from "lib/notify";
import { handleError } from "utils/error/handleError";
import { CLEANING_STATUS } from "lib/cleaningEvent/cleaningStatus";

interface LogRowProps {
  log: CleaningEventItem;
  refetchFunc?: () => Promise<any>;
  enableRobotName?: boolean;
  robots: CleaningRobotItem[];
  enableMapName?: boolean;
  maps: CleaningMapItem[];
  wrapperWidth: number;
  maxCleaningArea: number;
  enableMenu?: boolean;
}

const getStatusLabel = (status: string): string => {
  switch (status) {
    case CLEANING_STATUS.COMPLETED:
      return "完了";
    case CLEANING_STATUS.ERROR:
      return "エラー";
    case CLEANING_STATUS.CANCEL:
      return "キャンセル";
    case CLEANING_STATUS.PAUSED:
      return "一時停止";
    case CLEANING_STATUS.UNKNOWN:
      return "不明";
    default:
      return "-";
  }
};

export const LogRow = (props: LogRowProps) => {
  const {
    log,
    refetchFunc,
    enableRobotName,
    robots,
    enableMapName,
    maps,
    wrapperWidth,
    maxCleaningArea,
    enableMenu,
  } = props;
  const ref = useRef<HTMLTableRowElement>(null);
  const [robotName, setRobotName] = useState("");
  const [map, setMap] = useState<CleaningMapItem>();
  const [colSpan, setColSpan] = useState(7);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (enableMapName) {
      setColSpan((prev) => prev + 1);
    }

    if (enableRobotName) {
      setColSpan((prev) => prev + 1);
    }

    if (enableMenu) {
      setColSpan((prev) => prev + 1);
    }
  }, [enableMapName, enableRobotName, enableMenu]);

  useEffect(() => {
    setRobotName(
      robots.find((r) => r.robotId === log.robotId)?.robotName ||
        ROBOT_DELETED_LABEL
    );
    setMap(maps.find((m) => m.mapId === log.mapId));
  }, [log, maps, robots]);

  const onClickSync = async (
    e: React.MouseEvent<HTMLButtonElement> | undefined
  ) => {
    e?.stopPropagation();

    setLoading(true);
    try {
      await callSyncCleaningEvent();
      await refetchFunc?.();
      notify("更新しました");
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
    // setLoading(true);
    // syncLogsMutation.mutate(
    //   { buildingId, syncParameter: { logId: log.cleaningEventId } },
    //   {
    //     onSuccess: async () => {
    //       await refetchFunc?.();
    //       setLoading(false);
    //     },
    //     onError: () => {
    //       alert("再同期に失敗しました。。");
    //     },
    //   }
    // );
  };

  return (
    <>
      <TableRow
        key={log.cleaningEventId}
        onClick={() => setExpanded((prev) => !prev)}
        ref={ref}
        sx={{ position: "relative" }}
      >
        <TableCell>
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </TableCell>
        {enableRobotName && <TableCell>{robotName}</TableCell>}
        {enableMapName && (
          <TableCell>{map?.mapName || MAP_DELETED_LABEL}</TableCell>
        )}
        <TableCell>{format(log.cleaningStartTime, "M/d H:mm")}</TableCell>
        <TableCell>{format(log.cleaningEndTime, "M/d H:mm")}</TableCell>
        {/* differenceInMinutesで差分を計算して出力する */}
        <TableCell>
          {`${(
            "00" +
            Math.floor(
              differenceInMinutes(log.cleaningEndTime, log.cleaningStartTime) /
                60
            )
          ).slice(-2)}:${(
            "00" +
            (differenceInMinutes(log.cleaningEndTime, log.cleaningStartTime) %
              60)
          ).slice(-2)}`}
        </TableCell>
        <TableCell>{getStatusLabel(log.cleaningStatus)}</TableCell>
        <TableCell>{`${log.cleaningArea}`}&#13217;</TableCell>
        <TableCell>{`${
          !log.cleaningArea
            ? 0
            : Math.min(
                Math.round((log.cleaningArea / maxCleaningArea) * 100),
                100
              )
        }%`}</TableCell>
        {enableMenu && (
          <TableCell>
            <IconButton onClick={onClickSync}>
              <ReplayIcon sx={{ width: "30px", height: "30px" }} />
            </IconButton>
          </TableCell>
        )}
        {loading && (
          <TableCell
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
            }}
          >
            <Progress enabled />
          </TableCell>
        )}
      </TableRow>

      {log.imagePath && (
        <TableRow
          key={log.cleaningEventId + log.cleaningEventId}
          sx={{ overflowX: "clip" }}
        >
          <TableCell colSpan={colSpan} sx={{ py: 0 }}>
            <Collapse
              in={expanded}
              sx={{
                position: "sticky",
                left: "16px",
                width: `calc(${wrapperWidth}px - 32px)`,
                textAlign: "center",
              }}
            >
              <Box sx={{ pt: 1, pb: 2 }}>
                <MapViewer
                  src={log.imagePath}
                  displaySetting={
                    map?.displaySetting ?? DEFAULT_DISPLAY_SETTING
                  }
                />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
